import { apiClient } from '@core/api/ApiClientFactory.ts';
import { DateTime } from 'luxon';
import type { OperationMode } from '@/services';

// ============ TYPES ============

export interface Incident {
    incidentType: string;
    incidentTimestamp: string;
    incidentDevice: string;
    hasMailReceivers: boolean;
    operationMode: string;
}

export interface IncidentNotification {
    incidentType: string | null;
    incidentTemplateId: string | null;
    recipients: string[];
    isActiveOnStoreOperationModes: string[];
}

export interface IncidentTemplate {
    id: string;
    name: string;
    discriminator: string;
}

export interface AccessConfiguration {
    id?: string;
    name: string;
    authenticationProviderId: string;
    isActiveOnStoreOperationModes: string[];
}

export interface AuthenticationProvider {
    id: string | undefined;
    name: string;
    authenticationType: string;
    authorizationHeader?: string;
    url?: string;
    token?: string;
    disabled?: boolean;
    customProperties?: CustomProperty[];
    propertyMappings?: PropertyMappings[];
}

export interface CustomProperty {
    deviceId: string | null;
    customPropertiesJson: string;
}

export interface PropertyMappings {
    propertyName: string | null;
    mappingName: string;
}

// ============ INCIDENTS SERVICE ============

export const incidentsService = {
    // ============ Incidents Methods ============

    /**
     * Load incidents for a specific shop within a date range
     * @param shopId - Shop identifier
     * @param start - Start date/time (ISO format)
     * @param end - End date/time (ISO format)
     * @returns Promise with incidents data
     */
    async loadIncidents(shopId: string, start: string, end: string): Promise<Incident[]> {
        try {
            console.log(`[IncidentsService] Loading incidents for shop: ${shopId}`);
            const response = await apiClient.get<Incident[]>(
                `shopautomationstatistics/incidents/${shopId}/${start}/${end}`
            );
            console.log(`[IncidentsService] Incidents loaded:`, response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error loading incidents:', error);
            throw error;
        }
    },

    async exportIncidents(
        shopId: string,
        start: string,
        end: string,
        event: string[],
        source: string[]
    ): Promise<Blob> {
        try {
            const response = await apiClient.get(
                `shopautomationstatistics/export/${shopId}/${start}/${end}/${event.join(',')}/${source.join(',')}`,
                { responseType: 'blob' }
            );
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error loading incidents:', error);
            throw error;
        }
    },

    // ============ Templates Methods ============

    /**
     * Get all templates
     * @returns Promise with templates array
     */
    async getTemplates(): Promise<IncidentTemplate[]> {
        try {
            console.log('[IncidentsService] Loading templates...');
            const response = await apiClient.get<IncidentTemplate[]>('template');
            console.log(`[IncidentsService] Templates loaded:`, response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error loading templates:', error);
            throw error;
        }
    },

    /**
     * Get a single template by ID
     * @param id - Template ID
     * @returns Promise with template
     */
    async getTemplateById(id: string): Promise<IncidentTemplate> {
        try {
            console.log(`[IncidentsService] Loading template: ${id}`);
            const response = await apiClient.get<IncidentTemplate>(`template/${id}`);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error loading template:', error);
            throw error;
        }
    },

    /**
     * Create a new template
     * @param template - Template data
     * @returns Promise with created template
     */
    async createTemplate(template: IncidentTemplate): Promise<IncidentTemplate> {
        try {
            console.log('[IncidentsService] Creating template:', template);
            const response = await apiClient.post<IncidentTemplate>('template', template);
            console.log('[IncidentsService] Template created:', response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error creating template:', error);
            throw error;
        }
    },

    /**
     * Update an existing template
     * @param id - Template ID
     * @param template - Updated template data
     * @returns Promise with updated template
     */
    async updateTemplate(id: string, template: IncidentTemplate): Promise<IncidentTemplate> {
        try {
            console.log(`[IncidentsService] Updating template: ${id}`, template);
            const response = await apiClient.put<IncidentTemplate>(`template/${id}`, template);
            console.log('[IncidentsService] Template updated:', response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error updating template:', error);
            throw error;
        }
    },

    /**
     * Delete a template
     * @param id - Template ID
     * @returns Promise
     */
    async deleteTemplate(id: string): Promise<void> {
        try {
            console.log(`[IncidentsService] Deleting template: ${id}`);
            await apiClient.delete(`template/${id}`);
            console.log('[IncidentsService] Template deleted');
        } catch (error) {
            console.error('[IncidentsService] Error deleting template:', error);
            throw error;
        }
    },

    // ============ Operation Modes Methods ============

    /**
     * Get all operation modes
     * @returns Promise with operation modes array
     */
    async getOperationModes(): Promise<OperationMode[]> {
        try {
            console.log('[IncidentsService] Loading operation modes...');
            const response = await apiClient.get<OperationMode[]>('shopAutomation/operationModes');
            console.log(`[IncidentsService] Operation modes loaded:`, response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error loading operation modes:', error);
            throw error;
        }
    },

    /**
     * Get a single operation mode by ID
     * @param id - Operation mode ID
     * @returns Promise with operation mode
     */
    async getOperationModeById(id: string): Promise<OperationMode> {
        try {
            console.log(`[IncidentsService] Loading operation mode: ${id}`);
            const response = await apiClient.get<OperationMode>(`shopAutomation/operationModes/${id}`);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error loading operation mode:', error);
            throw error;
        }
    },

    /**
     * Create a new operation mode
     * @param operationMode - Operation mode data
     * @returns Promise with created operation mode
     */
    async createOperationMode(operationMode: OperationMode): Promise<OperationMode> {
        try {
            console.log('[IncidentsService] Creating operation mode:', operationMode);
            const response = await apiClient.post<OperationMode>('shopAutomation/operationModes', operationMode);
            console.log('[IncidentsService] Operation mode created:', response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error creating operation mode:', error);
            throw error;
        }
    },

    /**
     * Update an existing operation mode
     * @param id - Operation mode ID
     * @param operationMode - Updated operation mode data
     * @returns Promise with updated operation mode
     */
    async updateOperationMode(id: string, operationMode: OperationMode): Promise<OperationMode> {
        try {
            console.log(`[IncidentsService] Updating operation mode: ${id}`, operationMode);
            const response = await apiClient.put<OperationMode>(`shopAutomation/operationModes/${id}`, operationMode);
            console.log('[IncidentsService] Operation mode updated:', response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error updating operation mode:', error);
            throw error;
        }
    },

    /**
     * Delete an operation mode
     * @param id - Operation mode ID
     * @returns Promise
     */
    async deleteOperationMode(id: string): Promise<void> {
        try {
            console.log(`[IncidentsService] Deleting operation mode: ${id}`);
            await apiClient.delete(`shopAutomation/operationModes/${id}`);
            console.log('[IncidentsService] Operation mode deleted');
        } catch (error) {
            console.error('[IncidentsService] Error deleting operation mode:', error);
            throw error;
        }
    },

    // ============ Authentication Providers Methods ============

    /**
     * Get all authentication providers
     * @returns Promise with authentication providers array
     */
    async getAuthenticationProviders(): Promise<AuthenticationProvider[]> {
        try {
            console.log('[IncidentsService] Loading authentication providers...');
            const response = await apiClient.get<AuthenticationProvider[]>('authenticationProviders');
            console.log(`[IncidentsService] Authentication providers loaded:`, response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error loading authentication providers:', error);
            throw error;
        }
    },

    /**
     * Get enabled authentication providers only
     * @returns Promise with enabled authentication providers
     */
    async getEnabledAuthenticationProviders(): Promise<AuthenticationProvider[]> {
        try {
            const allProviders = await this.getAuthenticationProviders();
            return allProviders.filter((x) => x.disabled === false);
        } catch (error) {
            console.error('[IncidentsService] Error loading enabled authentication providers:', error);
            throw error;
        }
    },

    /**
     * Get a single authentication provider by ID
     * @param id - Authentication provider ID
     * @returns Promise with authentication provider
     */
    async getAuthenticationProviderById(id: string): Promise<AuthenticationProvider> {
        try {
            console.log(`[IncidentsService] Loading authentication provider: ${id}`);
            const response = await apiClient.get<AuthenticationProvider>(`authenticationProviders/${id}`);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error loading authentication provider:', error);
            throw error;
        }
    },

    /**
     * Create a new authentication provider
     * @param provider - Authentication provider data
     * @returns Promise with created authentication provider
     */
    async createAuthenticationProvider(provider: AuthenticationProvider): Promise<AuthenticationProvider> {
        try {
            console.log('[IncidentsService] Creating authentication provider:', provider);
            const response = await apiClient.post<AuthenticationProvider>('authenticationProviders', provider);
            console.log('[IncidentsService] Authentication provider created:', response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error creating authentication provider:', error);
            throw error;
        }
    },

    /**
     * Update an existing authentication provider
     * @param id - Authentication provider ID
     * @param provider - Updated authentication provider data
     * @returns Promise with updated authentication provider
     */
    async updateAuthenticationProvider(id: string, provider: AuthenticationProvider): Promise<AuthenticationProvider> {
        try {
            console.log(`[IncidentsService] Updating authentication provider: ${id}`, provider);
            const response = await apiClient.put<AuthenticationProvider>(`authenticationProviders/${id}`, provider);
            console.log('[IncidentsService] Authentication provider updated:', response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error updating authentication provider:', error);
            throw error;
        }
    },

    /**
     * Delete an authentication provider
     * @param id - Authentication provider ID
     * @returns Promise
     */
    async deleteAuthenticationProvider(id: string): Promise<void> {
        try {
            console.log(`[IncidentsService] Deleting authentication provider: ${id}`);
            await apiClient.delete(`authenticationProviders/${id}`);
            console.log('[IncidentsService] Authentication provider deleted');
        } catch (error) {
            console.error('[IncidentsService] Error deleting authentication provider:', error);
            throw error;
        }
    },

    // ============ Shop Automation Module Methods ============

    /**
     * Get all shop automation modules
     * @returns Promise with shop automation modules array
     */
    async getShopAutomationModules(): Promise<any[]> {
        try {
            console.log('[IncidentsService] Loading shop automation modules...');
            const response = await apiClient.get<any[]>('shopAutomation');
            console.log(`[IncidentsService] Shop automation modules loaded:`, response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error loading shop automation modules:', error);
            throw error;
        }
    },

    /**
     * Get a single shop automation module by ID
     * @param id - Shop automation module ID
     * @returns Promise with shop automation module
     */
    async getShopAutomationModuleById(id: string): Promise<any> {
        try {
            console.log(`[IncidentsService] Loading shop automation module: ${id}`);
            const response = await apiClient.get<any>(`shopAutomation/${id}`);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error loading shop automation module:', error);
            throw error;
        }
    },

    /**
     * Update a shop automation module
     * @param id - Shop automation module ID
     * @param module - Updated module data
     * @returns Promise with updated module
     */
    async updateShopAutomationModule(id: string, module: any): Promise<any> {
        try {
            console.log(`[IncidentsService] Updating shop automation module: ${id}`, module);
            const response = await apiClient.put<any>(`shopAutomation/${id}`, module);
            console.log('[IncidentsService] Shop automation module updated:', response.data);
            return response.data;
        } catch (error) {
            console.error('[IncidentsService] Error updating shop automation module:', error);
            throw error;
        }
    },

    /**
     * Send shop automation module to recycle bin
     * @param id - Shop automation module ID
     * @returns Promise
     */
    async sendShopAutomationModuleToRecycleBin(id: string): Promise<void> {
        try {
            console.log(`[IncidentsService] Sending shop automation module to recycle bin: ${id}`);
            await apiClient.post(`shopAutomation/${id}/send-to-recycle-bin`);
            console.log('[IncidentsService] Shop automation module sent to recycle bin');
        } catch (error) {
            console.error('[IncidentsService] Error sending shop automation module to recycle bin:', error);
            throw error;
        }
    },

    // ============ Helper Methods ============

    /**
     * Validate incident notification configuration
     * @param notification - Incident notification to validate
     * @param activeOperationModes - List of active operation modes
     * @returns Validation result with errors if any
     */
    validateIncidentNotification(
        notification: IncidentNotification,
        activeOperationModes: string[]
    ): { valid: boolean; errors: string[] } {
        const errors: string[] = [];

        if (!notification.incidentType) {
            errors.push('Incident type is required');
        }

        if (!notification.incidentTemplateId) {
            errors.push('Template is required');
        }

        if (!notification.recipients || notification.recipients.length === 0) {
            errors.push('At least one recipient is required');
        }

        if (!notification.isActiveOnStoreOperationModes || notification.isActiveOnStoreOperationModes.length === 0) {
            errors.push('At least one operation mode is required');
        }

        // Validate that selected operation modes are in active list
        const invalidModes = notification.isActiveOnStoreOperationModes.filter(
            (mode) => !activeOperationModes.includes(mode)
        );
        
        if (invalidModes.length > 0) {
            errors.push(`The following operation modes are not active: ${invalidModes.join(', ')}`);
        }

        return {
            valid: errors.length === 0,
            errors
        };
    },

    /**
     * Format incident timestamp for display
     * @param timestamp - ISO timestamp string
     * @returns Formatted date/time string
     */
    formatIncidentTimestamp(timestamp: string): string {
        if (!timestamp) return '';
        return DateTime.fromISO(timestamp).toLocaleString(DateTime.DATETIME_SHORT);
    },

    /**
     * Get unique incident types from incidents list
     * @param incidents - Array of incidents
     * @returns Array of unique incident type names
     */
    getUniqueIncidentTypes(incidents: Incident[]): string[] {
        const types = incidents
            .map((i) => i.incidentType)
            .map((i) => (i?.includes('OperationModeChanged') ? 'OperationModeChanged' : i))
            .filter(Boolean);
        return [...new Set(types)];
    },

    /**
     * Get unique incident devices from incidents list
     * @param incidents - Array of incidents
     * @returns Array of unique device names
     */
    getUniqueIncidentDevices(incidents: Incident[]): string[] {
        const devices = incidents.map((i) => i.incidentDevice).filter(Boolean);
        return [...new Set(devices)];
    },

    /**
     * Filter incidents by type and device
     * @param incidents - Array of incidents
     * @param selectedTypes - Selected incident types to filter by
     * @param selectedDevices - Selected devices to filter by
     * @returns Filtered incidents array
     */
    filterIncidents(incidents: Incident[], selectedTypes: string[], selectedDevices: string[]): Incident[] {
        return incidents.filter((item) => {
            const typeMatch =
                selectedTypes.length === 0 ||
                (selectedTypes.includes('OperationModeChanged')
                    ? item.incidentType?.includes('OperationModeChanged')
                    : selectedTypes.includes(item.incidentType));

            const deviceMatch = selectedDevices.length === 0 || selectedDevices.includes(item.incidentDevice);

            return typeMatch && deviceMatch;
        });
    }
};
